import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ErrorService } from '@app/services/error.service';
import { catchError, map } from 'rxjs/operators';
import { EmbedLog, Log, LogWithContent, embed as logEmbed, configuration as logConfiguration, EmbedLogWithContent } from '@data/models/audit.model';
import { Observable } from 'rxjs';
import { Embedded } from '@data/models/embed.model';
import { uniq } from 'lodash';
import { toParams } from '@shared/utils/query-to-params';

const AUDIT_BASE_URL = '/audit/v1';

export interface AuditPagination {
    page: number;
    limit: number;
}

export interface AuditFilters {
    updatedFields?: string[];
    range?: {
        from?: Date;
        to?: Date;
    };
    updatedBy?: string;
}

export interface AuditFilterParams {
    filter?: Record<string, string>;
    page: number;
    limit: number;
    sort?: string;
    from?: string;
    to?: string;
    who?: string;
    updatedFields?: string;
}

interface LogResourceEntity {
    resourceId: string;
    entity: string;
}

@Injectable()
export class AuditService {

    private errors;

    constructor(
        private errorService: ErrorService,
        private http: HttpClient,
    ) {
        this.errors = this.errorService.auditServiceErrors;
    }

    public getFilteredLogs<T extends logEmbed>(
        logResourceEntity: LogResourceEntity,
        filterParams: AuditFilterParams,
        embedFields: T[],
    ): Observable<EmbedLog<T>[]> {
        return this.getLogs(logResourceEntity, filterParams, embedFields);
    }

    public getLog<T extends logEmbed>(
        { entity, resourceId, logId }: { entity: string; resourceId: string; logId: string },
        embedFields: T[],
    ): Observable<EmbedLogWithContent<T>> {
        const embeds = uniq(embedFields.map((it) => logConfiguration[it].embed));
        const params = toParams({ embed: embeds.length > 0 && embeds.join(',') });

        return this.http.get<{ log: LogWithContent; embedded: Embedded }>(`${AUDIT_BASE_URL}/logs/${entity}/${resourceId}/${logId}`, { params }).pipe(
            map(({ log, embedded }) => LogWithContent.toLogWithContentEmbedded(log, embedded, embedFields)),
            catchError(this.errorService.errorHandler<EmbedLogWithContent<T>>(this.errors, 'Récupération d\'un log'))
        );
    }

    public getNextLog<T extends logEmbed>(
        logResourceEntity: LogResourceEntity,
        fromDate: string,
        filterParams: AuditFilterParams,
        embedFields: T[],
    ): Observable<EmbedLog<T> | undefined> {
        const params = {
            updatedFields: filterParams.updatedFields,
            to: filterParams.to,
            who: filterParams.who,
            sort: 'when',
            from: fromDate,
            limit: 2
        };

        return this.getLogs(logResourceEntity, params, embedFields).pipe(
            map(logs => logs[1])
        );
    }

    public getPreviousLog<T extends logEmbed>(
        logResourceEntity: LogResourceEntity,
        fromDate: string,
        filterParams: AuditFilterParams,
        embedFields: T[],
    ): Observable<EmbedLog<T> | undefined> {
        const params = {
            updatedFields: filterParams.updatedFields,
            from: filterParams.from,
            who: filterParams.who,
            sort: '-when',
            to: fromDate,
            limit: 2
        };

        return this.getLogs(logResourceEntity, params, embedFields).pipe(
            map(logs => logs[1])
        );
    }

    private getLogs<T extends logEmbed>(
        { entity, resourceId }: LogResourceEntity,
        queryParams: object,
        embedFields: T[],
    ): Observable<EmbedLog<T>[]> {
        const embeds = uniq(embedFields.map((it) => logConfiguration[it].embed));
        const params = toParams({ ...queryParams, embed: embeds.length > 0 && embeds.join(',') });

        return this.http.get<{ logs: Log[]; embedded: Embedded }>(
            `${AUDIT_BASE_URL}/logs/${entity}/${params.get('filter')?.includes('resource.') ? '' : resourceId}`,
            { params }
        ).pipe(
            map(({ logs, embedded }) => Log.toLogsEmbedded(logs, embedded, embedFields)),
            catchError(this.errorService.errorHandler<EmbedLog<T>[]>(this.errors, 'Récupération des logs', []))
        );
    }
}

